import React, { useCallback, useEffect, useMemo, useState } from "react";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useNavigate } from "react-router-dom";
import "./NotificationsPanel.scss";
import { useAuth } from "../context/AuthContext";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../services/notificationApi";

function formatNotificationDate(value) {
  if (!value) return "Maintenant";

  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "Maintenant";
  }
}

function getUserIdentity(user) {
  return {
    userId: user?.userId || user?.id || "",
    deviceId: user?.device_id || user?.deviceId || "",
  };
}

function NotificationsPanel({ open, onClose, onUnreadChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const identity = useMemo(() => getUserIdentity(user), [user]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (!identity.userId && !identity.deviceId) {
      setItems([]);
      onUnreadChange?.(0);
      return;
    }

    try {
      setLoading(true);
      const payload = await fetchNotifications({
        userId: identity.userId,
        deviceId: identity.deviceId,
        limit: 14,
      });

      setItems(payload.data || []);
      onUnreadChange?.(payload.unreadCount || 0);
    } catch (error) {
      console.error("Notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [identity.deviceId, identity.userId, onUnreadChange]);

  useEffect(() => {
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 30000);
    return () => window.clearInterval(timer);
  }, [loadNotifications]);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [loadNotifications, open]);

  const openNotification = async (notification) => {
    try {
      if (!notification.is_read) {
        await markNotificationRead(notification.id, identity);
        await loadNotifications();
      }

      if (notification.action_route) {
        navigate(notification.action_route);
        onClose?.();
      }
    } catch (error) {
      console.error("Lecture notification:", error);
    }
  };

  const markAllRead = async () => {
    try {
      await markAllNotificationsRead(identity);
      await loadNotifications();
    } catch (error) {
      console.error("Notifications read all:", error);
    }
  };

  if (!open) return null;

  return (
    <aside className="notifications-panel">
      <div className="notifications-panel__header">
        <div>
          <p>Centre notifications</p>
          <h3>Ce qui bouge sur Streamflix</h3>
        </div>
        <button onClick={markAllRead} aria-label="Tout marquer comme lu">
          <DoneAllIcon />
        </button>
      </div>

      <div className="notifications-panel__list">
        {loading && !items.length ? <div className="notifications-panel__empty">Chargement...</div> : null}

        {!loading && !items.length ? (
          <div className="notifications-panel__empty">
            <NotificationsNoneIcon />
            <p>Aucune notification pour le moment.</p>
          </div>
        ) : null}

        {items.map((notification) => (
          <button
            key={notification.id}
            className={`notification-item ${notification.is_read ? "" : "notification-item--unread"}`}
            onClick={() => openNotification(notification)}
          >
            <span className="notification-item__dot" />
            <span className="notification-item__body">
              <strong>{notification.title || "Notification"}</strong>
              <small>{formatNotificationDate(notification.created_at)}</small>
              <p>{notification.message || "Nouvelle activite disponible."}</p>
            </span>
          </button>
        ))}
      </div>
    </aside>
  );
}

export default NotificationsPanel;
